import { fetchJSON as fetchAPI } from './client';

export type WarehouseCapacityPlan = {
  id: string;
  name: string;
  status: string;
  warehouse_id?: string;
  warehouse_name?: string;
  forecast_units: number;
  storage_utilization: number;
  receiving_utilization: number;
  dispatch_utilization: number;
  dock_utilization: number;
  labor_utilization: number;
  throughput_units: number;
  capacity_buffer: number;
  health_score: number;
  fulfillment_readiness_score: number;
  resilience_index: number;
  storage_optimization_score: number;
  constraint_count: number;
};

export type CapacityModel = {
  id: string;
  capacity_type: string;
  resource_id: string;
  resource_name: string;
  total_capacity: number;
  allocated_capacity: number;
  available_capacity: number;
  utilization_pct: number;
  constraint_active: boolean;
};

export type StoragePlan = {
  id: string;
  zone_id: string;
  zone_name: string;
  storage_type: string;
  total_slots: number;
  allocated_slots: number;
  utilization_pct: number;
  abc_class?: string;
  velocity_tier?: string;
  optimization_note?: string;
};

export type DockPlan = {
  id: string;
  dock_id: string;
  dock_name: string;
  direction: string;
  scheduled_slots: number;
  total_slots: number;
  utilization_pct: number;
  conflict_count: number;
  carrier_name?: string;
};

export type LaborPlan = {
  id: string;
  shift_name: string;
  role: string;
  headcount: number;
  required_headcount: number;
  productivity_pct: number;
  utilization_pct: number;
  labor_cost: number;
  overtime_hours: number;
};

export type WarehouseConstraint = {
  id: string;
  type: string;
  resource_name?: string;
  severity: string;
  probability: number;
  description: string;
  impact: string;
  mitigation: string;
};

export type WarehouseRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  business_explanation: string;
  confidence: number;
  expected_savings: number;
  throughput_improvement_pct?: number;
  service_impact?: string;
  auto_execute: boolean;
  status: string;
};

export type WarehouseScenario = {
  id: string;
  name: string;
  scenario_type: string;
  throughput: number;
  storage_utilization: number;
  labor_utilization: number;
  cost: number;
  service_level: number;
  fulfillment_readiness: number;
  description?: string;
};

export type DigitalTwin = {
  id: string;
  warehouse_id: string;
  warehouse_name: string;
  layout_zones: number;
  total_storage: number;
  total_docks: number;
  labor_headcount: number;
  equipment_count: number;
  bottleneck_nodes: string[];
  utilization_map: Record<string, number>;
};

export type WarehouseDashboard = {
  active_plans: number;
  avg_storage_utilization: number;
  avg_dock_utilization: number;
  avg_labor_productivity: number;
  capacity_buffer: number;
  throughput: number;
  fulfillment_readiness_score: number;
  health_score: number;
  resilience_index: number;
  constraint_count: number;
  pending_recommendations: number;
  storage_optimization_score: number;
};

const API_BASE = import.meta.env.VITE_WAREHOUSECAPACITYPLANNING_API ?? '/api/v1';

function whFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const warehouseCapacityPlanningApi = {
  dashboard: () => whFetch<WarehouseDashboard>('/warehouse-dashboard'),
  listPlans: () => whFetch<WarehouseCapacityPlan[]>('/warehouse-capacity'),
  listStoragePlans: (planId?: string) => whFetch<StoragePlan[]>(`/storage-plans${planId ? `?plan_id=${planId}` : ''}`),
  listDockPlans: (planId?: string) => whFetch<DockPlan[]>(`/dock-plans${planId ? `?plan_id=${planId}` : ''}`),
  listLaborPlans: (planId?: string) => whFetch<LaborPlan[]>(`/labor-plans${planId ? `?plan_id=${planId}` : ''}`),
  listConstraints: (planId?: string) => whFetch<WarehouseConstraint[]>(`/warehouse-constraints${planId ? `?plan_id=${planId}` : ''}`),
  listRecommendations: (planId?: string) => whFetch<WarehouseRecommendation[]>(`/warehouse-recommendations${planId ? `?plan_id=${planId}` : ''}`),
  listScenarios: (planId?: string) => whFetch<WarehouseScenario[]>(`/warehouse-scenarios${planId ? `?plan_id=${planId}` : ''}`),
  getDigitalTwin: (planId: string) => whFetch<DigitalTwin>(`/warehouse-digital-twin?plan_id=${planId}`),
  calculateCapacity: (planId: string) =>
    whFetch<{ fulfillment_readiness_score: number; explanation: string }>('/capacity/calculate', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    }),
  approvePlan: (id: string) => whFetch<WarehouseCapacityPlan>(`/warehouse-capacity/${id}/approve`, { method: 'POST' }),
};
